import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

function getSessionId() {
  let sid = localStorage.getItem("ergosafe_chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("ergosafe_chat_sid", sid);
  }
  return sid;
}

const WELCOME_FR = "Bonjour ! Je suis l'assistant ALDOL-SOLUTIONS. Matériel ergonomique, éthylotests, exosquelettes, formations… dites-moi ce que vous cherchez et je vous oriente.";
const WELCOME_EN = "Hello! I'm the ALDOL-SOLUTIONS assistant. Ergonomic equipment, breathalyzers, exoskeletons, training… tell me what you're looking for and I'll point you in the right direction.";

export function ChatWidget() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: lang === "fr" ? WELCOME_FR : WELCOME_EN }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: getSessionId(), message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, suggestions: data.suggestions || [], formations: data.formation_suggestions || [] }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, je suis momentanément indisponible. Réessayez dans un instant ou contactez-nous via le formulaire de contact." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen((v) => !v)} data-testid="chat-toggle-btn" aria-label="Assistant conseil"
        className="fixed bottom-5 right-5 z-[55] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-200 hover:scale-105"
        style={{ background: "var(--orange)", color: "var(--bone)" }}>
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[55] w-[calc(100vw-2.5rem)] max-w-sm flex flex-col overflow-hidden shadow-2xl"
            style={{ background: "var(--bone)", border: "1px solid var(--border)", height: "480px" }}
            data-testid="chat-panel">
            <div className="px-5 py-4 flex items-center gap-3" style={{ background: "var(--ink)", color: "var(--bone)" }}>
              <MessageCircle size={18} style={{ color: "var(--orange)" }} />
              <div>
                <p className="font-display font-bold leading-none">Assistant ALDOL-SOLUTIONS</p>
                <p className="text-[11px] mt-1" style={{ color: "rgba(245,245,240,0.6)" }}>Conseil produits & formations</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-lenis-prevent data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className="max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                    data-testid={`chat-msg-${m.role}-${i}`}
                    style={m.role === "user"
                      ? { background: "var(--orange)", color: "var(--bone)" }
                      : { background: "var(--white)", border: "1px solid var(--border)", color: "var(--ink)" }}>
                    {m.content}
                  </div>
                  {(m.suggestions?.length > 0 || m.formations?.length > 0) && (
                    <div className="mt-2 space-y-1.5 w-full max-w-[85%]" data-testid={`chat-suggestions-${i}`}>
                      {m.suggestions?.map((p) => (
                        <Link key={p.id} to={`/catalogue/${p.id}`} onClick={() => setOpen(false)}
                          className="flex items-center gap-2.5 p-2 transition-colors duration-150 hover:bg-black/5"
                          style={{ background: "var(--white)", border: "1px solid var(--orange)" }}
                          data-testid={`chat-suggestion-${p.id}`}>
                          <img src={p.image} alt={p.name} loading="lazy" className="w-9 h-9 object-cover shrink-0" style={{ border: "1px solid var(--border)" }} />
                          <span className="flex-1 text-xs font-semibold leading-tight line-clamp-2">{p.name}</span>
                          <span className="text-xs font-display font-black whitespace-nowrap" style={{ color: "var(--orange)" }}>
                            {p.price > 0 ? `${p.price.toFixed(0)} €` : "Devis"}
                          </span>
                        </Link>
                      ))}
                      {m.formations?.map((f) => (
                        <Link key={f.id} to={`/formations/${f.id}`} onClick={() => setOpen(false)}
                          className="flex items-center gap-2.5 p-2 transition-colors duration-150 hover:bg-black/5"
                          style={{ background: "var(--white)", border: "1px solid var(--ink)" }}
                          data-testid={`chat-formation-${f.id}`}>
                          <img src={f.image} alt={f.title} loading="lazy" className="w-9 h-9 object-cover shrink-0" style={{ border: "1px solid var(--border)" }} />
                          <span className="flex-1 text-xs font-semibold leading-tight line-clamp-2">{f.title}</span>
                          <span className="text-[10px] uppercase font-bold whitespace-nowrap" style={{ color: "var(--ink-2)" }}>Formation</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-3.5 py-2.5" style={{ background: "var(--white)", border: "1px solid var(--border)" }}>
                    <Loader2 size={16} className="animate-spin" style={{ color: "var(--orange)" }} />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="flex gap-2 p-3" style={{ borderTop: "1px solid var(--border)", background: "var(--white)" }}>
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("Votre question…", "Your question…")}
                className="flex-1 px-3 py-2.5 text-sm outline-none" style={{ background: "var(--bone)", border: "1px solid var(--border)" }}
                data-testid="chat-input" />
              <button type="submit" disabled={loading} className="btn-primary px-4" data-testid="chat-send-btn" aria-label="Envoyer">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
